import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Mail,
  ArrowLeft,
  Heart,
  ArrowRight,
  Lock,
  User,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { useApp } from '../context';
import { authApi, setToken, ApiError, AuthResponse } from '../lib/api';
import {
  signInWithGoogle,
  signInWithApple,
  checkOAuthRedirectResult,
  isOAuthRedirectPending,
} from '../lib/firebase';

interface LoginProps {
  onBack: () => void;
  onComplete: (isNewUser: boolean) => void;
  onShowLegal: (page: 'privacy' | 'terms') => void;
}

type Step = 'email' | 'login' | 'register' | 'register-name' | 'forgot';

export default function Login({ onBack, onComplete, onShowLegal }: LoginProps) {
  const { state, setUser } = useApp();
  const hasGuestData = state.isGuestMode && state.contacts.length > 0;
  const [email, setEmail] = useState('');
  const [userName, setUserName] = useState(''); // name from DB for returning users
  const [step, setStep] = useState<Step>('email');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const pendingProvider = isOAuthRedirectPending()
    ? (localStorage.getItem('nudge_oauth_pending') as 'google' | 'apple')
    : null;
  const [loadingAction, setLoadingAction] = useState<
    'email' | 'login' | 'register' | 'google' | 'apple' | 'forgot' | null
  >(pendingProvider);
  const [error, setError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  // Pending OAuth login for existing account confirmation
  const [pendingOAuth, setPendingOAuth] = useState<{
    token: string;
    user: AuthResponse['user'];
  } | null>(null);

  const handleEmailNext = async () => {
    if (!email.trim()) return;
    setLoadingAction('email');
    setError('');

    try {
      const result = await authApi.checkEmail(email.trim());
      if (result.exists) {
        setUserName(result.name || '');
        setStep('login');
      } else {
        setStep('register');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Connection failed. Please try again.');
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleLogin = async () => {
    if (!password) {
      setError('Password is required');
      return;
    }

    setLoadingAction('login');
    setError('');

    try {
      const res = await authApi.login({ email: email.trim(), password });
      setToken(res.token);
      setUser(
        {
          id: res.user.id,
          name: res.user.name,
          email: res.user.email,
          isGuest: false,
          roles: res.user.roles,
          timezone: res.user.timezone,
          weeklyGoalTarget: res.user.weeklyGoalTarget,
          photoUrl: res.user.photoUrl,
          referralCode: res.user.referralCode,
        },
        { skipSync: true },
      );

      // Register push token after login (user gesture context)
      if ('Notification' in window) {
        console.log('[push] Login complete, permission:', Notification.permission);
        import('../lib/firebase').then(({ requestNotificationPermission, refreshPushToken }) => {
          if (Notification.permission === 'granted') {
            refreshPushToken().catch((err) => console.error('[push] refresh error:', err));
          } else if (Notification.permission === 'default') {
            requestNotificationPermission().catch((err) =>
              console.error('[push] request error:', err),
            );
          } else {
            console.warn('[push] Permission denied — cannot register');
          }
        });
      } else {
        console.warn('[push] Notification API not available in this browser');
      }

      onComplete(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Connection failed. Please try again.');
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRegisterPassword = () => {
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(password)) {
      setError('Password must contain an uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      setError('Password must contain a lowercase letter');
      return;
    }
    if (!/\d/.test(password)) {
      setError('Password must contain a number');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setStep('register-name');
  };

  const handleRegister = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setLoadingAction('register');
    setError('');

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const referralCode = localStorage.getItem('nudge_referral_code') || undefined;
      const res = await authApi.register({
        name: name.trim(),
        email: email.trim(),
        password,
        timezone,
        referral_code: referralCode,
      });
      setToken(res.token);
      setUser({
        id: res.user.id,
        name: res.user.name,
        email: res.user.email,
        isGuest: false,
        roles: res.user.roles,
        timezone: res.user.timezone,
        weeklyGoalTarget: res.user.weeklyGoalTarget,
        photoUrl: res.user.photoUrl,
        referralCode: res.user.referralCode,
      });

      localStorage.removeItem('nudge_referral_code');
      document.cookie = 'nudge_ref=; path=/; max-age=0';

      // Register push token after registration (user gesture context)
      if ('Notification' in window) {
        console.log('[push] Registration complete, permission:', Notification.permission);
        import('../lib/firebase').then(({ requestNotificationPermission, refreshPushToken }) => {
          if (Notification.permission === 'granted') {
            refreshPushToken().catch((err) => console.error('[push] refresh error:', err));
          } else if (Notification.permission === 'default') {
            requestNotificationPermission().catch((err) =>
              console.error('[push] request error:', err),
            );
          }
        });
      }

      onComplete(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Connection failed. Please try again.');
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) return;
    setLoadingAction('forgot');
    setError('');
    try {
      await authApi.forgotPassword(email.trim());
      setForgotSent(true);
    } catch {
      // Always show success to prevent email enumeration
      setForgotSent(true);
    } finally {
      setLoadingAction(null);
    }
  };

  const completeOAuthFlow = async (idToken: string, provider: 'google' | 'apple') => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const referralCode = localStorage.getItem('nudge_referral_code') || undefined;
    const res =
      provider === 'google'
        ? await authApi.google({ id_token: idToken, timezone, referral_code: referralCode })
        : await authApi.apple({ id_token: idToken, timezone, referral_code: referralCode });

    // If existing account + guest has data, pause for confirmation
    if (!res.isNewUser && hasGuestData) {
      setPendingOAuth({
        token: res.token,
        user: res.user,
      });
      return;
    }

    setToken(res.token);
    setUser(
      {
        id: res.user.id,
        name: res.user.name,
        email: res.user.email,
        isGuest: false,
        roles: res.user.roles,
        timezone: res.user.timezone,
        weeklyGoalTarget: res.user.weeklyGoalTarget,
        photoUrl: res.user.photoUrl,
        referralCode: res.user.referralCode,
      },
      { skipSync: !res.isNewUser },
    );
    if (res.isNewUser) {
      localStorage.removeItem('nudge_referral_code');
      document.cookie = 'nudge_ref=; path=/; max-age=0';
    }
    onComplete(res.isNewUser);
  };

  // Check for OAuth redirect result on mount (mobile/PWA redirect flow)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await checkOAuthRedirectResult();
        if (cancelled || !result) return;
        setLoadingAction(result.provider);
        await completeOAuthFlow(result.idToken, result.provider);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Sign-in failed. Please try again.');
        }
      } finally {
        if (!cancelled) setLoadingAction(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOAuthLogin = async (provider: 'google' | 'apple') => {
    setLoadingAction(provider);
    setError('');

    try {
      const idToken = provider === 'google' ? await signInWithGoogle() : await signInWithApple();

      // null means redirect was initiated — page will reload
      if (!idToken) return;

      await completeOAuthFlow(idToken, provider);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof Error && err.message.includes('popup-closed')) {
        // User closed the popup — not an error
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setLoadingAction(null);
    }
  };

  const confirmPendingOAuth = () => {
    if (!pendingOAuth) return;
    setToken(pendingOAuth.token);
    setUser(
      {
        id: pendingOAuth.user.id,
        name: pendingOAuth.user.name,
        email: pendingOAuth.user.email,
        isGuest: false,
        roles: pendingOAuth.user.roles,
        timezone: pendingOAuth.user.timezone,
        weeklyGoalTarget: pendingOAuth.user.weeklyGoalTarget,
        photoUrl: pendingOAuth.user.photoUrl,
        referralCode: pendingOAuth.user.referralCode,
      },
      { skipSync: true },
    );

    if ('Notification' in window) {
      import('../lib/firebase').then(({ requestNotificationPermission, refreshPushToken }) => {
        if (Notification.permission === 'granted') {
          refreshPushToken().catch((err) => console.error('[push] refresh error:', err));
        } else if (Notification.permission === 'default') {
          requestNotificationPermission().catch((err) =>
            console.error('[push] request error:', err),
          );
        }
      });
    }

    setPendingOAuth(null);
    onComplete(false);
  };

  const goBack = () => {
    if (pendingOAuth) {
      setPendingOAuth(null);
      return;
    }
    if (step === 'register-name') {
      setStep('register');
      setError('');
      return;
    }
    if (step === 'login' || step === 'register') {
      setStep('email');
      setPassword('');
      setConfirmPassword('');
      setError('');
    } else if (step === 'forgot') {
      setStep('login');
      setForgotSent(false);
      setError('');
    } else {
      onBack();
    }
  };

  const headingText = () => {
    if (pendingOAuth) return `Welcome back, ${pendingOAuth.user.name}!`;
    switch (step) {
      case 'email':
        return 'Sign in to Nudge';
      case 'login':
        return `Welcome back${userName ? `, ${userName}` : ''}!`;
      case 'register':
        return 'Create your account';
      case 'register-name':
        return 'One last thing!';
      case 'forgot':
        return 'Reset Password';
    }
  };

  const subtitleText = () => {
    if (pendingOAuth) return 'This account already has data on our servers.';
    switch (step) {
      case 'email':
        return 'Stay connected with the people who matter most.';
      case 'login':
        return 'Enter your password to continue.';
      case 'register':
        return 'Choose a secure password.';
      case 'register-name':
        return 'What should we call you?';
      case 'forgot':
        return 'Check your inbox for instructions.';
    }
  };

  return (
    <div className="h-dvh bg-[#f8f5f5] flex flex-col max-w-md mx-auto overflow-hidden">
      <header className="p-6 flex items-center justify-between relative z-10">
        <button
          onClick={goBack}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
      </header>

      <div className="flex-1 flex flex-col px-8 relative z-10 overflow-hidden">
        <div className="mb-4">
          <div className="size-16 rounded-[2rem] bg-[#fa8585] flex items-center justify-center text-white shadow-xl shadow-[#fa8585]/30 mb-4">
            <Heart size={32} fill="white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">{headingText()}</h1>
          <p className="text-slate-500 text-sm">{subtitleText()}</p>
        </div>

        <AnimatePresence mode="wait">
          {step === 'email' && !pendingOAuth && (
            <motion.div
              key="email-step"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}
              <div className="space-y-3">
                <div className="relative">
                  <Mail
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="email"
                    name="username"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleEmailNext()}
                    placeholder="Email address"
                    autoFocus
                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={handleEmailNext}
                  disabled={loadingAction !== null || !email.trim()}
                  className="w-full bg-slate-900 text-white font-bold py-3 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {loadingAction === 'email' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Checking...
                    </>
                  ) : (
                    <>
                      Continue with Email <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </div>

              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-[#f8f5f5] px-4 text-slate-400 font-medium uppercase tracking-widest">
                    Or
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => handleOAuthLogin('google')}
                  disabled={loadingAction !== null}
                  className="w-full bg-white border border-slate-200 text-slate-700 font-bold py-3 rounded-2xl flex items-center justify-center gap-3 hover:bg-slate-50 active:scale-[0.98] transition-all shadow-sm disabled:opacity-50"
                >
                  {loadingAction === 'google' ? (
                    <Loader2 size={20} className="animate-spin text-slate-400" />
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M23.04 12.2614C23.04 11.4459 22.9668 10.6614 22.8309 9.90686H12V14.3569H18.1891C17.9223 15.795 17.1118 17.0164 15.8945 17.8318V20.7136H19.6109C21.7855 18.7118 23.04 15.7636 23.04 12.2614Z"
                        fill="#4285F4"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 23.4999C15.105 23.4999 17.7082 22.4704 19.6109 20.7136L15.8945 17.8318C14.8645 18.5222 13.545 18.9272 12 18.9272C9.00909 18.9272 6.46909 16.9045 5.565 14.1849H1.72363V17.164C3.615 20.9249 7.50454 23.4999 12 23.4999Z"
                        fill="#34A853"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M5.56501 14.1852C5.33501 13.4947 5.20455 12.7575 5.20455 12.0002C5.20455 11.2429 5.33501 10.5057 5.56501 9.81523V6.83618H1.72364C0.945003 8.38618 0.5 10.1429 0.5 12.0002C0.5 13.8575 0.945003 15.6143 1.72364 17.1643L5.56501 14.1852Z"
                        fill="#FBBC05"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M12 5.07273C13.6923 5.07273 15.2045 5.65455 16.3964 6.79091L20.6945 2.49273C17.7082 0.948182 15.105 0 12 0C7.50454 0 3.615 2.575 1.72363 6.33614L5.565 9.31523C6.46909 6.59568 9.00909 5.07273 12 5.07273Z"
                        fill="#EA4335"
                      />
                    </svg>
                  )}
                  {loadingAction === 'google' ? 'Connecting...' : 'Continue with Google'}
                </button>

                <button
                  onClick={() => setError('Apple sign-in coming soon!')}
                  className="w-full bg-slate-900/50 border border-slate-900/50 text-white/70 font-bold py-4 rounded-2xl flex items-center px-5 transition-all shadow-sm cursor-default"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-3"
                  >
                    <path
                      d="M16.9942 9.00693C17.0141 6.58287 18.986 5.39702 19.0805 5.34024C17.9543 3.69345 16.1824 3.41407 15.5861 3.33633C14.1165 3.18731 12.6569 4.20485 11.9015 4.20485C11.1362 4.20485 9.94541 3.35105 8.69493 3.37617C7.0748 3.40201 5.564 4.3168 4.73413 5.7602C3.04043 8.70566 4.30058 13.0483 5.95267 15.432C6.76166 16.5997 7.71212 17.9152 8.95663 17.8654C10.1585 17.8155 10.6276 17.085 12.0673 17.085C13.507 17.085 13.9317 17.8654 15.1837 17.8396C16.4808 17.8137 17.3001 16.6508 18.1147 15.4625C19.043 14.1066 19.4239 12.7877 19.4449 12.7153C19.4186 12.7032 17.0425 11.7779 16.9942 9.00693Z"
                      fill="white"
                    />
                    <path
                      d="M14.4751 2.05204C15.1485 1.23849 15.5997 0.119792 15.4746 -1C14.4996 0.0384501 13.3103 0.654877 12.597 1.51322C11.9682 2.26189 11.4191 3.41113 11.5714 4.51268C12.668 4.59664 13.7845 3.90547 14.4751 2.05204Z"
                      fill="white"
                    />
                  </svg>
                  Continue with Apple
                  <span className="ml-auto text-[10px] font-normal text-white/40">Coming Soon</span>
                </button>
              </div>
            </motion.div>
          )}

          {step === 'login' && (
            <motion.div
              key="login-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-2xl mb-2 shadow-sm">
                <div className="size-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-400">
                  {email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{email}</p>
                </div>
                <button
                  onClick={() => {
                    setStep('email');
                    setPassword('');
                    setError('');
                  }}
                  className="text-xs font-bold text-[#fa8585] px-3 py-1.5 hover:bg-[#fa8585]/10 rounded-lg transition-colors"
                >
                  Edit
                </button>
              </div>

              {hasGuestData && (
                <div className="bg-amber-50 text-amber-800 p-3 rounded-xl text-sm font-medium border border-amber-200">
                  Signing into an existing account will replace your local data. To transfer your
                  contacts and interactions, go back and create a new account instead.
                </div>
              )}

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Hidden email so password managers associate credentials */}
                <input
                  type="email"
                  name="username"
                  autoComplete="email"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  className="sr-only"
                  aria-hidden="true"
                />
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                    placeholder="Password"
                    autoFocus
                    className="w-full bg-white border border-slate-200 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={handleLogin}
                  disabled={loadingAction !== null || !password}
                  className="w-full bg-[#fa8585] text-white font-bold py-4 rounded-2xl shadow-lg shadow-[#fa8585]/30 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
                >
                  {loadingAction === 'login' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>

              <div className="text-center pt-4">
                <button
                  onClick={() => setStep('forgot')}
                  className="text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </motion.div>
          )}

          {step === 'register' && (
            <motion.div
              key="register-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-2xl mb-2 shadow-sm">
                <div className="size-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-400">
                  {email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{email}</p>
                </div>
                <button
                  onClick={() => {
                    setStep('email');
                    setPassword('');
                    setConfirmPassword('');
                    setName('');
                    setError('');
                  }}
                  className="text-xs font-bold text-[#fa8585] px-3 py-1.5 hover:bg-[#fa8585]/10 rounded-lg transition-colors"
                >
                  Edit
                </button>
              </div>

              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                {/* Hidden email so password managers associate email + password */}
                <input
                  type="email"
                  name="username"
                  autoComplete="email"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  className="sr-only"
                  aria-hidden="true"
                />
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="password"
                    name="new-password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    autoFocus
                    className="w-full bg-white border border-slate-200 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <div className="relative">
                  <Lock
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="password"
                    name="new-password-confirm"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRegisterPassword()}
                    placeholder="Confirm password"
                    className="w-full bg-white border border-slate-200 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={handleRegisterPassword}
                  disabled={loadingAction !== null || !password || password !== confirmPassword}
                  className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight size={18} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 'register-name' && (
            <motion.div
              key="register-name-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm font-medium border border-red-100">
                  {error}
                </div>
              )}

              <div className="space-y-3">
                <div className="relative">
                  <User
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                    size={20}
                  />
                  <input
                    type="text"
                    name="display-name"
                    autoComplete="off"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                    placeholder="Your name"
                    autoFocus
                    className="w-full bg-white border border-slate-200 rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={handleRegister}
                  disabled={loadingAction !== null || !name.trim()}
                  className="w-full bg-[#fa8585] text-white font-bold py-4 rounded-2xl shadow-lg shadow-[#fa8585]/30 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 mt-2 flex items-center justify-center gap-2"
                >
                  {loadingAction === 'register' ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
                <p className="text-center text-[10px] text-slate-400 font-medium mt-3 leading-relaxed">
                  By creating an account, you agree to our{' '}
                  <button onClick={() => onShowLegal('terms')} className="underline text-slate-500">
                    Terms of Service
                  </button>{' '}
                  and{' '}
                  <button
                    onClick={() => onShowLegal('privacy')}
                    className="underline text-slate-500"
                  >
                    Privacy Policy
                  </button>
                </p>
              </div>
            </motion.div>
          )}

          {step === 'forgot' && (
            <motion.div
              key="forgot-step"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {forgotSent ? (
                <>
                  <div className="bg-emerald-50 text-emerald-600 p-4 rounded-2xl text-sm font-bold text-center border border-emerald-100 mb-6">
                    If an account exists with {email}, we've sent a password reset link.
                  </div>
                  <button
                    onClick={() => {
                      setStep('login');
                      setForgotSent(false);
                    }}
                    className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  >
                    <ArrowLeft size={18} /> Back to Login
                  </button>
                </>
              ) : (
                <>
                  <p className="text-slate-500 text-sm">
                    We'll send a reset link to{' '}
                    <span className="font-bold text-slate-700">{email}</span>.
                  </p>
                  <button
                    onClick={handleForgotPassword}
                    disabled={loadingAction !== null}
                    className="w-full bg-[#fa8585] text-white font-bold py-4 rounded-2xl shadow-lg shadow-[#fa8585]/30 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {loadingAction === 'forgot' ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </button>
                  <button
                    onClick={() => setStep('login')}
                    className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  >
                    <ArrowLeft size={18} /> Back to Login
                  </button>
                </>
              )}
            </motion.div>
          )}
          {pendingOAuth && (
            <motion.div
              key="oauth-confirm"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3 p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
                <div className="size-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-400">
                  {pendingOAuth.user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {pendingOAuth.user.name}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{pendingOAuth.user.email}</p>
                </div>
              </div>

              <div className="bg-amber-50 text-amber-800 p-4 rounded-xl text-sm font-medium border border-amber-200 flex gap-3">
                <AlertTriangle size={20} className="text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold mb-1">Your local data will be lost</p>
                  <p className="text-amber-700 text-xs leading-relaxed">
                    You have {state.contacts.length} contact{state.contacts.length !== 1 && 's'} and{' '}
                    {state.interactions.length} interaction{state.interactions.length !== 1 && 's'}{' '}
                    saved locally. Signing into this account will replace them with your existing
                    account data.
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <button
                  onClick={confirmPendingOAuth}
                  className="w-full bg-[#fa8585] text-white font-bold py-4 rounded-2xl shadow-lg shadow-[#fa8585]/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
                >
                  Sign In Anyway
                </button>
                <button
                  onClick={goBack}
                  className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={18} /> Go Back
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
